import React, { useState, useEffect } from "react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { toast } from "sonner";

export default function MasterDialog({ title, fields, open, onOpenChange, initial = {}, onSubmit, submitLabel = "Save" }) {
  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (open) setForm(initial || {}); /* eslint-disable-next-line */ }, [open]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    for (const f of fields) {
      if (f.required && !form[f.name]) { toast.error(`${f.label} is required`); return; }
    }
    setSaving(true);
    try {
      await onSubmit(form);
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    } finally { setSaving(false); }
  };

  const opts = (o) => (typeof o === "string" ? { value: o, label: o } : o);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-xl" data-testid="master-dialog">
        <DialogHeader><DialogTitle className="font-display">{title}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          {fields.map((f) => (
            <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
              <Label className="text-xs text-zinc-400">{f.label}{f.required && " *"}</Label>
              <div className="mt-1">
                {f.type === "select" ? (
                  <Select value={form[f.name] || ""} onValueChange={(v) => set(f.name, v)}>
                    <SelectTrigger data-testid={`mf-${f.name}`} className="bg-zinc-950"><SelectValue placeholder={`Select ${f.label}`} /></SelectTrigger>
                    <SelectContent className="max-h-72">
                      {(f.options || []).map(opts).map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                ) : f.type === "textarea" ? (
                  <Textarea data-testid={`mf-${f.name}`} value={form[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} className="bg-zinc-950" />
                ) : (
                  <Input data-testid={`mf-${f.name}`} type={f.type === "number" ? "number" : f.type === "date" ? "date" : "text"}
                    value={form[f.name] ?? ""}
                    onChange={(e) => set(f.name, f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
                    className="bg-zinc-950" />
                )}
              </div>
            </div>
          ))}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={submit} disabled={saving} data-testid="master-submit" className="bg-amber-500 font-bold text-zinc-950 hover:bg-amber-400">
            {saving ? "Saving..." : submitLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
